import { BudgetPhases, DEFAULT_BUDGET_BY, BudgetBys } from 'business/constants';
import { AreaT, BasePivotFieldT } from 'business/definitions';
import { getRejectedInscriptionFiltersByPhase } from 'routes/BudgetModule/helpers/query';
import {
    getAmountFieldForPhase,
    isActiveBudgetPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { getResourceSoftwareIdField } from '../helpers/budgetProposalHelpers';
import {
    BUDGET_YEAR_FIELD,
    REALIZED_FIELD,
    VOTED_FIELD,
    REQUESTED_FIELD,
    VOTED_TOTAL_FIELD,
    PERCENT_REALIZED_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    INSCRIPTION_EXPENSE_INCOME,
    BUDGET_PROPOSAL_VENTILATION_BY_OPERATION,
    BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE,
    REPORTED_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
    SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
} from './constants';
import { StatusesSettingsT } from './types';

export type BudgetProposalVentilationBy =
    | typeof BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE
    | typeof BUDGET_PROPOSAL_VENTILATION_BY_OPERATION;

export const getRawBudgetVentilatedQuery = (
    budgetProposalBy: BudgetBys = DEFAULT_BUDGET_BY,
    ventilationBy: BudgetProposalVentilationBy,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): QueryT => {
    const filters: QueryFilterT[] = [];
    if (isActiveBudgetPhase(phase)) {
        filters.push(
            ...getRejectedInscriptionFiltersByPhase(phase, statusesSettings),
        );
    }

    const dimsPerVentilationType: Record<
        BudgetProposalVentilationBy,
        string[]
    > = {
        resource: [getResourceSoftwareIdField(budgetProposalBy)],
        operation: [
            SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
            SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
            SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
        ],
    };

    return {
        metrics: [{ field: getAmountFieldForPhase(phase), aggregation: 'sum' }],
        dimensions: [
            {
                field: INSCRIPTION_EXPENSE_INCOME,
                displayIndex: 0,
            },
            ...dimsPerVentilationType[ventilationBy].map((dim, i) => ({
                field: dim,
                displayIndex: i + 1,
            })),
        ],
        filters,
    };
};

export const makeMetric = (
    metrics: string[],
    aggregation: string,
): QueryMetricT[] =>
    metrics.map((metric) => ({
        field: metric,
        aggregation,
    }));
export const IMPUTATION_METRICS = [
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
    REALIZED_FIELD,
    REQUESTED_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    REPORTED_FIELD,
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    PERCENT_REALIZED_FIELD,
];

export const EXERCICE_METRICS = [REALIZED_FIELD, VOTED_FIELD];

export const getExtendedImputationsQuery = (
    additionalDimension: string[] = [],
    additionalMetrics: string[] = [],
    additionalFilters: QueryFilterT[] = [],
): QueryT => ({
    metrics: [...makeMetric(additionalMetrics, 'sum')],

    dimensions: [
        {
            field: BUDGET_YEAR_FIELD,
            displayIndex: 0,
        },
        ...additionalDimension.map((dim, index) => ({
            field: dim,
            displayIndex: index + 1,
        })),
    ],

    filters: additionalFilters,
});

const dimensionToPivotField = (
    dim: QueryDimensionT,
    index: number,
    area: AreaT,
): BasePivotFieldT => ({
    dataField: dim.field.replace('.', ''),
    area,
    areaIndex: index,
    summaryType: '',
});

const metricToPivotField = (
    metric: QueryMetricT,
    index: number,
): BasePivotFieldT => ({
    dataField: metric.field.replace('.', ''),
    area: 'data',
    areaIndex: index,
    summaryType: 'sum',
});

export const getPivotQuerySettings = (
    query: QueryT,
    dimensionsToPivot: string[],
    dimsToHide: readonly string[],
): BasePivotFieldT[] => {
    let settings = [];
    const metricFields = query.metrics.map((metricObj, index) =>
        metricToPivotField(metricObj, index),
    );
    settings = [...metricFields];
    const pivotedDimFields = query.dimensions
        .filter(
            (dim) =>
                dimensionsToPivot.includes(dim.field) &&
                !dimsToHide.includes(dim.field),
        )
        .map((dimObj, index) =>
            dimensionToPivotField(dimObj, settings.length + index, 'column'),
        );
    settings = [...settings, ...pivotedDimFields];
    const rowDimFields = query.dimensions
        .filter(
            (dim) =>
                !dimensionsToPivot.includes(dim.field) &&
                !dimsToHide.includes(dim.field),
        )
        .map((dimObj, index) =>
            dimensionToPivotField(dimObj, settings.length + index, 'row'),
        );
    settings = [...settings, ...rowDimFields];
    const hiddenDimFields = query.dimensions
        .filter((dim) => dimsToHide.includes(dim.field))
        .map((dimObj, index) =>
            dimensionToPivotField(dimObj, settings.length + index, null),
        );
    return [...settings, ...hiddenDimFields];
};
